import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Check, X, RefreshCw, ExternalLink, ChevronLeft, Factory, FileText } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { adminClient } from '../lib/adminClient';

/**
 * /admin/suppliers/claims
 * --------------------------------------------------------------------------
 * Admin queue for supplier-listing claims. Pending claims first.
 * Approve → server-side `admin_approve_supplier_claim(claim_id, true, note)`
 *   inserts a row in supplier_users granting access to the supplier dashboard.
 * Reject → updates claim status only.
 */

const Pill = ({ tone = 'neutral', children }) => {
  const tones = {
    pending:  'border-amber-500 bg-amber-50 text-amber-900',
    approved: 'border-emerald-600 bg-emerald-50 text-emerald-900',
    rejected: 'border-neutral-400 bg-neutral-100 text-neutral-600',
    neutral:  'border-neutral-300 bg-neutral-50 text-neutral-700',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
};

const fmtDate = (iso) => !iso ? '—' : new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

const ClaimRow = ({ claim, busyId, onReview }) => {
  const [showReject, setShowReject] = useState(false);
  const [note, setNote] = useState('');
  const isBusy = busyId === claim.id;

  return (
    <div data-testid={`admin-claim-row-${claim.id}`} className="border border-black bg-white p-4 md:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <h3 className="font-display text-lg">{claim.supplier_name}</h3>
            <span className="font-mono-data text-[10px] tracking-wider text-neutral-500">{claim.supplier_slug}</span>
            <Pill tone={claim.status}>{claim.status}</Pill>
          </div>
          <div className="mt-2 text-sm text-neutral-700">
            From <span className="font-semibold">{claim.contact_email}</span>
            {claim.contact_role ? <> · {claim.contact_role}</> : null}
          </div>
          {claim.proof_url && (
            <a href={claim.proof_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs underline">
              Proof of association <ExternalLink size={10} strokeWidth={1.5} />
            </a>
          )}
          {claim.reason && <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700">{claim.reason}</p>}
          <div className="mt-2 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">filed {fmtDate(claim.created_at)}</div>
        </div>

        {claim.status === 'pending' && (
          <div className="flex flex-col gap-2">
            <button
              data-testid={`admin-claim-approve-${claim.id}`}
              disabled={isBusy}
              onClick={() => onReview(claim, true, 'Approved by admin.')}
              className="inline-flex items-center gap-1.5 border border-emerald-700 bg-emerald-600 px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {isBusy ? <Loader2 size={10} strokeWidth={1.5} className="animate-spin" /> : <Check size={10} strokeWidth={1.5} />} Approve
            </button>
            <button
              data-testid={`admin-claim-reject-${claim.id}`}
              disabled={isBusy}
              onClick={() => setShowReject(!showReject)}
              className="inline-flex items-center gap-1.5 border border-black bg-white px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider text-black hover:bg-black hover:text-white disabled:opacity-50"
            >
              <X size={10} strokeWidth={1.5} /> Reject
            </button>
          </div>
        )}
      </div>

      {showReject && claim.status === 'pending' && (
        <div className="mt-4 border-t border-neutral-200 pt-4">
          <label className="label-overline text-neutral-500">Reason for rejection (optional)</label>
          <input
            data-testid={`admin-claim-reject-note-${claim.id}`}
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. No verifiable association with this factory."
            className="mt-1 w-full border border-black bg-transparent px-3 py-2 text-sm outline-none"
          />
          <button
            data-testid={`admin-claim-confirm-reject-${claim.id}`}
            disabled={isBusy}
            onClick={() => onReview(claim, false, note || 'Rejected.')}
            className="mt-2 inline-flex items-center gap-1.5 border border-red-700 bg-red-600 px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isBusy ? <Loader2 size={10} strokeWidth={1.5} className="animate-spin" /> : <X size={10} strokeWidth={1.5} />} Confirm rejection
          </button>
        </div>
      )}

      {claim.status !== 'pending' && claim.decision_note && (
        <p className="mt-3 border-t border-neutral-200 pt-3 text-xs text-neutral-600">Note: {claim.decision_note}</p>
      )}
    </div>
  );
};

// ── ApplicationRow ───────────────────────────────────────────────────
const ApplicationRow = ({ app, busyId, onReview }) => {
  const [showReject, setShowReject] = useState(false);
  const [note, setNote] = useState('');
  const isBusy = busyId === app.id;
  const tone = app.status === 'onboarded' ? 'approved' : app.status === 'rejected' ? 'rejected' : 'pending';
  return (
    <div data-testid={`admin-app-row-${app.id}`} className="border border-black bg-white p-4 md:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <h3 className="font-display text-lg">{app.factory_name}</h3>
            {app.country && <span className="font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">· {app.country}</span>}
            <Pill tone={tone}>{app.status}</Pill>
          </div>
          <div className="mt-2 text-sm text-neutral-700">
            <span className="font-semibold">{app.contact_name}</span> · <span>{app.contact_email}</span>
            {app.contact_phone ? <> · {app.contact_phone}</> : null}
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-neutral-700 md:grid-cols-3">
            {app.city && <><dt className="label-overline text-neutral-500">City</dt><dd>{app.city}</dd></>}
            {app.founded_year && <><dt className="label-overline text-neutral-500">Founded</dt><dd>{app.founded_year}</dd></>}
            {app.employees_range && <><dt className="label-overline text-neutral-500">Employees</dt><dd>{app.employees_range}</dd></>}
            {app.moq_min != null && <><dt className="label-overline text-neutral-500">MOQ min</dt><dd>{app.moq_min}</dd></>}
            {app.monthly_capacity && <><dt className="label-overline text-neutral-500">Monthly capacity</dt><dd>{app.monthly_capacity}</dd></>}
            {app.selected_tier && <><dt className="label-overline text-neutral-500">Tier requested</dt><dd className="capitalize">{app.selected_tier}</dd></>}
            {app.primary_specialty && <><dt className="label-overline text-neutral-500">Specialty</dt><dd className="col-span-2 md:col-span-2">{app.primary_specialty}</dd></>}
            {Array.isArray(app.categories) && app.categories.length > 0 && (
              <><dt className="label-overline text-neutral-500">Categories</dt><dd className="col-span-1 md:col-span-2">{app.categories.join(' · ')}</dd></>
            )}
            {Array.isArray(app.existing_certifications) && app.existing_certifications.length > 0 && (
              <><dt className="label-overline text-neutral-500">Certifications</dt><dd className="col-span-1 md:col-span-2">{app.existing_certifications.join(' · ')}</dd></>
            )}
          </dl>
          {app.website_url && (
            <a href={app.website_url} target="_blank" rel="noreferrer" data-testid={`admin-app-website-${app.id}`}
               className="mt-2 inline-flex items-center gap-1 text-xs underline">
              Website <ExternalLink size={10} strokeWidth={1.5} />
            </a>
          )}
          {app.buyer_references && <p className="mt-2 whitespace-pre-wrap text-xs text-neutral-700"><span className="label-overline text-neutral-500">References:</span> {app.buyer_references}</p>}
          {app.notes && <p className="mt-2 whitespace-pre-wrap text-xs text-neutral-700"><span className="label-overline text-neutral-500">Notes:</span> {app.notes}</p>}
          <div className="mt-2 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">filed {fmtDate(app.created_at)}</div>
        </div>

        {app.status !== 'onboarded' && app.status !== 'rejected' && (
          <div className="flex flex-col gap-2">
            <button
              data-testid={`admin-app-approve-${app.id}`}
              disabled={isBusy}
              onClick={() => onReview(app, true, 'Approved — supplier listing created.')}
              className="inline-flex items-center gap-1.5 border border-emerald-700 bg-emerald-600 px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {isBusy ? <Loader2 size={10} strokeWidth={1.5} className="animate-spin" /> : <Check size={10} strokeWidth={1.5} />} Onboard
            </button>
            <button
              data-testid={`admin-app-reject-${app.id}`}
              disabled={isBusy}
              onClick={() => setShowReject(!showReject)}
              className="inline-flex items-center gap-1.5 border border-black bg-white px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider text-black hover:bg-black hover:text-white disabled:opacity-50"
            >
              <X size={10} strokeWidth={1.5} /> Reject
            </button>
          </div>
        )}

        {app.status === 'onboarded' && (
          <Link
            to={`/supplier/${(app.notes || '').match(/slug:([\w-]+)/)?.[1] || ''}`}
            data-testid={`admin-app-view-listing-${app.id}`}
            className="self-start inline-flex items-center gap-1 border border-black px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider hover:bg-black hover:text-white"
          >
            View listing <ExternalLink size={10} strokeWidth={1.5} />
          </Link>
        )}
      </div>

      {showReject && app.status !== 'onboarded' && app.status !== 'rejected' && (
        <div className="mt-4 border-t border-neutral-200 pt-4">
          <label className="label-overline text-neutral-500">Reason for rejection (optional)</label>
          <input
            data-testid={`admin-app-reject-note-${app.id}`}
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Production capacity below platform minimum."
            className="mt-1 w-full border border-black bg-transparent px-3 py-2 text-sm outline-none"
          />
          <button
            data-testid={`admin-app-confirm-reject-${app.id}`}
            disabled={isBusy}
            onClick={() => onReview(app, false, note || 'Rejected.')}
            className="mt-2 inline-flex items-center gap-1.5 border border-red-700 bg-red-600 px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isBusy ? <Loader2 size={10} strokeWidth={1.5} className="animate-spin" /> : <X size={10} strokeWidth={1.5} />} Confirm rejection
          </button>
        </div>
      )}
    </div>
  );
};

const AdminSupplierClaims = () => {
  const [mode, setMode] = useState('claims'); // 'claims' | 'applications'
  const [claims, setClaims] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('pending'); // 'pending' | 'approved' | 'rejected' | 'all'

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [cs, as] = await Promise.all([
        adminClient.listClaims().catch(() => []),
        adminClient.listApplications().catch(() => []),
      ]);
      setClaims(cs); setApps(as);
    } catch (e) { toast.error(e?.message || 'Could not load.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Claims filtering ────────────────────────────────────────────
  const claimsFiltered = useMemo(
    () => filter === 'all' ? claims : claims.filter(c => c.status === filter),
    [claims, filter]
  );
  const claimsCounts = useMemo(() => ({
    pending:  claims.filter(c => c.status === 'pending').length,
    approved: claims.filter(c => c.status === 'approved').length,
    rejected: claims.filter(c => c.status === 'rejected').length,
  }), [claims]);

  // ── Applications filtering ──────────────────────────────────────
  // Submitted + in_review = "new". Onboarded = approved. Rejected stays.
  const appsFiltered = useMemo(() => {
    if (filter === 'all') return apps;
    if (filter === 'pending')  return apps.filter(a => a.status === 'submitted' || a.status === 'in_review');
    if (filter === 'approved') return apps.filter(a => a.status === 'onboarded' || a.status === 'approved');
    if (filter === 'rejected') return apps.filter(a => a.status === 'rejected');
    return apps;
  }, [apps, filter]);
  const appsCounts = useMemo(() => ({
    pending:  apps.filter(a => a.status === 'submitted' || a.status === 'in_review').length,
    approved: apps.filter(a => a.status === 'onboarded' || a.status === 'approved').length,
    rejected: apps.filter(a => a.status === 'rejected').length,
  }), [apps]);

  const counts = mode === 'claims' ? claimsCounts : appsCounts;
  const items  = mode === 'claims' ? claimsFiltered : appsFiltered;

  const reviewClaim = async (claim, approve, note) => {
    setBusyId(claim.id);
    try {
      await adminClient.reviewClaim(claim.id, approve, note);
      toast.success(approve ? `Approved — supplier_users row created for ${claim.contact_email}` : 'Claim rejected.');
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'Could not update claim.');
    } finally { setBusyId(null); }
  };

  const reviewApp = async (app, approve, note) => {
    setBusyId(app.id);
    try {
      const data = await adminClient.reviewApplication(app.id, approve, note);
      if (approve && data?.supplier_slug) {
        toast.success(`Onboarded — listing created at /supplier/${data.supplier_slug}${data?.auth_user_linked ? ' (auth user linked)' : ''}`);
      } else if (!approve) {
        toast.success('Application rejected.');
      } else {
        toast.success('Application processed.');
      }
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'Could not update application.');
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-claims-page" className="bg-[#F5F5F0]">
      <SEO title="Supplier claims — Admin" noindex />

      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-8 md:px-8 md:py-10">
          <Link to="/admin/rfqs" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-neutral-500 hover:text-black">
            <ChevronLeft size={12} strokeWidth={1.5} /> Operations Center
          </Link>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,4vw,3.25rem)', lineHeight: 0.95 }}>Supplier onboarding.</h1>
              <p className="mt-2 text-sm text-neutral-600">Approve claims from factories on existing listings, or onboard brand-new factories from supplier applications.</p>
            </div>
            <button data-testid="admin-claims-refresh" onClick={() => { setLoading(true); refresh(); }} className="border border-black bg-white p-2 hover:bg-black hover:text-white">
              <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* Mode switch: Claims vs Applications */}
          <div className="mt-5 flex flex-wrap gap-2 border-b border-neutral-300 pb-3" data-testid="admin-mode-switch">
            <button
              data-testid="admin-mode-claims"
              onClick={() => { setMode('claims'); setFilter('pending'); }}
              className={`inline-flex items-center gap-2 border px-3 py-2 font-mono-data text-[11px] uppercase tracking-wider ${mode === 'claims' ? 'border-black bg-black text-white' : 'border-black bg-white hover:bg-black hover:text-white'}`}
            >
              <Factory size={12} strokeWidth={1.5} /> Claims (existing listings) · {claimsCounts.pending + claimsCounts.approved + claimsCounts.rejected}
            </button>
            <button
              data-testid="admin-mode-applications"
              onClick={() => { setMode('applications'); setFilter('pending'); }}
              className={`inline-flex items-center gap-2 border px-3 py-2 font-mono-data text-[11px] uppercase tracking-wider ${mode === 'applications' ? 'border-black bg-black text-white' : 'border-black bg-white hover:bg-black hover:text-white'}`}
            >
              <FileText size={12} strokeWidth={1.5} /> Applications (new factories) · {appsCounts.pending + appsCounts.approved + appsCounts.rejected}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {[['pending',  `${mode === 'applications' ? 'New' : 'Pending'} · ${counts.pending}`],
              ['approved', `${mode === 'applications' ? 'Onboarded' : 'Approved'} · ${counts.approved}`],
              ['rejected', `Rejected · ${counts.rejected}`],
              ['all',      'All']].map(([k, l]) => (
              <button key={k} data-testid={`admin-claims-filter-${k}`} onClick={() => setFilter(k)}
                className={`border px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider ${filter === k ? 'border-black bg-black text-white' : 'border-black hover:bg-black hover:text-white'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1440px] px-5 py-6 md:px-8">
          {loading ? (
            <div className="my-12 text-center"><Loader2 size={20} strokeWidth={1.5} className="mx-auto animate-spin text-neutral-500" /></div>
          ) : items.length === 0 ? (
            <div data-testid="admin-claims-empty" className="border border-dashed border-neutral-300 bg-white p-10 text-center text-sm text-neutral-500">
              No {filter !== 'all' ? filter : ''} {mode === 'applications' ? 'applications' : 'claims'} to review.
            </div>
          ) : (
            <div className="space-y-3" data-testid={`admin-${mode}-list`}>
              {mode === 'claims'
                ? items.map((c) => <ClaimRow key={c.id} claim={c} busyId={busyId} onReview={reviewClaim} />)
                : items.map((a) => <ApplicationRow key={a.id} app={a} busyId={busyId} onReview={reviewApp} />)
              }
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminSupplierClaims;
